<script setup lang="ts">
import {ref} from "vue";
import {BriefcaseBusiness, Clock3, Euro, FileText, GraduationCap, Send, ShieldCheck, Users, Wrench} from "lucide-vue-next";

import "@/assets/styles/career.css";


const showForm = ref(false);

const selectedJob = ref("Dachdecker");

const openForm = (job: string) => {
  selectedJob.value = job;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
};
</script>

<template>

  <section class="career">

    <!-- HERO -->
    <div class="career-hero">

      <div class="career-hero-content">

        <span class="section-badge">
          Karriere im Dachdeckerhandwerk
        </span>

        <h1>
          Arbeiten, wo man abends sieht, was man geschafft hat.
        </h1>

        <p>
          Bei uns geht es um saubere Dächer, ein verlässliches Team und Projekte,
          auf die du stolz sein kannst: Sanierungen, Flachdächer, Sturmschäden
          und moderne Dachsysteme in der Region.
        </p>

        <div class="career-hero-points">
          <span><ShieldCheck/> Festanstellung möglich</span>
          <span><Wrench/> Moderne Ausstattung</span>
          <span><Clock3/> Klare Abläufe</span>
        </div>

      </div>

      <div class="career-hero-image">
        <img src="/images/career/ausbildung-v2.png" alt="Auszubildende und erfahrener Dachdecker arbeiten gemeinsam"/>
      </div>

    </div>

    <!-- BENEFITS -->
    <div class="career-benefits">

      <div class="benefit-card">
        <Users/>
        <h3>Starkes Team</h3>
        <p>Kollegiales Arbeiten, kurze Wege und Menschen, die aufeinander achten.</p>
      </div>

      <div class="benefit-card">
        <Euro/>
        <h3>Faire Bezahlung</h3>
        <p>Leistung soll sich lohnen: zuverlässig, transparent und mit Perspektive.</p>
      </div>

      <div class="benefit-card">
        <GraduationCap/>
        <h3>Weiterentwicklung</h3>
        <p>Du lernst dazu, übernimmst Verantwortung und wächst im Handwerk.</p>
      </div>

    </div>

    <div class="career-story">
      <div>
        <span class="section-badge">Was dich erwartet</span>
        <h2>Kein anonymer Großbetrieb. Kein Chaos auf Zuruf.</h2>
      </div>

      <p>
        Wir arbeiten strukturiert, halten Baustellen sauber und sprechen offen.
        Du bekommst klare Aufgaben, vernünftiges Werkzeug und ein Team, das
        Qualität ernst nimmt. Genau so wollen Kunden bedient werden und genau so
        macht Handwerk auch langfristig Spaß.
      </p>
    </div>

    <!-- JOBS -->
    <div class="jobs-section">

      <div class="jobs-header">
        <h2>Offene Stellen</h2>
        <p>Bewirb dich unkompliziert. Ein kurzer erster Kontakt reicht.</p>
      </div>

      <div class="jobs-grid">

        <div class="job-card">

          <div class="job-icon">
            <BriefcaseBusiness/>
          </div>

          <h3>Dachdecker (m/w/d)</h3>

          <p>
            Verstärke unser Team im Bereich Steildach,
            Flachdach, Sanierung und Reparatur.
          </p>

          <ul>
            <li>Erfahrung im Dachdeckerhandwerk</li>
            <li>Saubere, zuverlässige Arbeitsweise</li>
            <li>Führerschein von Vorteil</li>
          </ul>

          <button @click="openForm('Dachdecker')" class="apply-btn">
            Jetzt bewerben
          </button>

        </div>

        <div class="job-card">

          <div class="job-icon">
            <FileText/>
          </div>

          <h3>Ausbildung Dachdecker</h3>

          <p>
            Starte deine Ausbildung im Handwerk
            mit Zukunft und Perspektive.
          </p>

          <ul>
            <li>Interesse an praktischer Arbeit</li>
            <li>Teamgeist und Lernbereitschaft</li>
            <li>Begleitung durch erfahrene Kollegen</li>
          </ul>

          <button @click="openForm('Ausbildung')" class="apply-btn">
            Ausbildung starten
          </button>

        </div>

      </div>

    </div>

    <!-- POPUP -->
    <div
        v-if="showForm"
        class="popup-overlay"
        @click.self="closeForm"
    >

      <div class="popup-box">

        <button class="close-btn" @click="closeForm">
          ×
        </button>

        <h2>
          Bewerbung als {{ selectedJob }}
        </h2>

        <form class="career-form">

          <div class="form-grid">

            <div class="form-group">
              <label>Vorname</label>
              <input type="text" placeholder="Max"/>
            </div>

            <div class="form-group">
              <label>Nachname</label>
              <input type="text" placeholder="Mustermann"/>
            </div>

          </div>

          <div class="form-group">
            <label>E-Mail</label>
            <input type="email" placeholder="mail@example.de"/>
          </div>

          <div class="form-group">
            <label>Telefon</label>
            <input type="text" placeholder="+49 ..."/>
          </div>

          <div class="form-group">
            <label>Nachricht</label>
            <textarea
                rows="5"
                placeholder="Erzähl uns etwas über dich..."
            />
          </div>

          <div class="upload-grid">

            <label class="upload-box">
              Lebenslauf hochladen
              <input type="file" hidden/>
            </label>

            <label class="upload-box">
              Anschreiben hochladen
              <input type="file" hidden/>
            </label>

          </div>

          <button type="submit" class="submit-btn">

            <Send/>

            Bewerbung senden

          </button>

        </form>

      </div>

    </div>

  </section>

</template>

<style scoped>
</style>
